import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormik } from 'formik';
import type { SelectChangeEvent } from '@mui/material';
import {
  FormControl, InputLabel, Link, MenuItem, Paper, Select, Stack, Typography,
} from '@mui/material';
import { EmissionsFilterLogic } from '../../emissons-components/overview/emissions-filter/emissions-filter-logic';
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service';
import ConversionService from '../../../../shared/services/conversion/conversion.service';
import CategoryService from '../../../../shared/services/category/category.service';
import GroupService from '../../../../shared/services/group/group.service';
import MeasurementUnitService from '../../../../shared/services/measurement-unit/measurement-unit.service';
import { IConversion } from '../../../../shared/services/conversion/conversion.model';
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model';
import { IGroupWithCategory } from '../../../../shared/services/group/group.model';
import { sortGroupsByGhgCategory, formatGroupLabel } from '../../../../shared/constants/ghg-category';

/** A picked conversion factor plus the emission inventory group it was chosen under. */
export interface ConversionSelection {
  conversionId: string;
  /** Emission inventory group id; the backend requires it whenever conversionId is set. */
  groupId: string;
  measurementUnitId?: string;
  measurementUnitName?: string;
  conversion: IConversion;
}

/** Formik fields shared with `EmissionsFilterLogic` (same cascade as manual activity entry). */
function emptyConversionFormikValues() {
  return {
    groupId: '',
    categoryId: '',
    conversionSourceId: '',
    detailLevelOne: '',
    detailLevelTwo: '',
    detailLevelThree: '',
    detailLevelFour: '',
    detailLevelFive: '',
    measurementUnitName: '',
    measurementUnitId: '' as string | number,
    returnId: '',
  };
}

function humanConversionPath(c: IConversion): string {
  const parts = [c.detailLevelOne, c.detailLevelTwo, c.detailLevelThree, c.detailLevelFour, c.detailLevelFive]
    .filter((s): s is string => typeof s === 'string' && Boolean(s.trim()));
  const primary = parts.length > 0 ? parts.join(' · ') : '';
  const scopeSuffix = c.scope ? ` • Scope ${c.scope}` : '';
  return `${primary}${scopeSuffix}` || '—';
}

function formatMultiplier(n: number | undefined): string {
  if (n == null || Number.isNaN(n)) return '—';
  if (Math.abs(n) >= 1) return n.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 8 });
  return n.toLocaleString(undefined, { maximumSignificantDigits: 8 });
}

interface Props {
  /** Hydrate from a saved rule's conversion; the picker loads the details itself. */
  initialConversionId?: string | null;
  onChange: (selection: ConversionSelection | null) => void;
  /** Factor year the cascade filters by. */
  calendarYear: string;
  disabled?: boolean;
}

/**
 * Group, factor source, then the detail-level cascade, ending in a summary
 * card of the chosen factor. The selection confirms itself as soon as the cascade
 * resolves a factor; "Change conversion factor" reopens the cascade.
 */
export const ConversionFactorPicker = ({ initialConversionId, onChange, calendarYear, disabled = false }: Props) => {
  const conversionSourceService = useMemo(() => new ConversionSourceService(), []);
  const conversionService = useMemo(() => new ConversionService(), []);
  const categoryService = useMemo(() => new CategoryService(), []);
  const measurementUnitService = useMemo(() => new MeasurementUnitService(), []);
  const groupService = useMemo(() => new GroupService(), []);

  const formik = useFormik({ initialValues: emptyConversionFormikValues(), onSubmit: () => { } });

  const [conversionSources, setConversionSources] = useState<IConversionSource[]>([]);
  const [emissionInventoryGroups, setEmissionInventoryGroups] = useState<IGroupWithCategory[]>([]);
  const [confirmed, setConfirmed] = useState<ConversionSelection | null>(null);
  const [browsing, setBrowsing] = useState(false);
  const [filterIsEdit, setFilterIsEdit] = useState(false);
  const hydratedForRef = useRef<string | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    groupService.getGroupsWithCategories(false)
      .then(setEmissionInventoryGroups)
      .catch(() => setEmissionInventoryGroups([]));
    conversionSourceService.getAll(true, 'conversion')
      .then(setConversionSources)
      .catch(() => setConversionSources([]));
  }, [groupService, conversionSourceService]);

  // Hydrate from a saved rule's conversion id.
  useEffect(() => {
    const target = initialConversionId ?? null;
    if (hydratedForRef.current === target) return;
    hydratedForRef.current = target;
    if (!target) {
      formik.setValues(emptyConversionFormikValues());
      setConfirmed(null);
      setBrowsing(false);
      setFilterIsEdit(false);
      onChangeRef.current(null);
      return;
    }
    let cancelled = false;
    Promise.all([
      conversionService.getConversionByPk(target),
      categoryService.getAllCategories(),
      measurementUnitService.getAll(),
    ])
      .then(([conv, allCategories, measurementUnits]) => {
        if (cancelled) return;
        const category = allCategories.find(c => c.id === conv.categoryId);
        const unitName = measurementUnits.find(u => u.id === conv.measurementUnitId)?.name ?? '';
        formik.setValues({
          ...emptyConversionFormikValues(),
          conversionSourceId: conv.conversionSourceId ?? '',
          groupId: category?.groupId ?? '',
          categoryId: conv.categoryId,
          detailLevelOne: conv.detailLevelOne ?? '',
          detailLevelTwo: conv.detailLevelTwo ?? '',
          detailLevelThree: conv.detailLevelThree ?? '',
          detailLevelFour: conv.detailLevelFour ?? '',
          detailLevelFive: conv.detailLevelFive ?? '',
          measurementUnitId: conv.measurementUnitId ?? '',
          measurementUnitName: unitName,
          returnId: conv.id,
        });
        const selection: ConversionSelection = {
          conversionId: conv.id,
          groupId: category?.groupId ?? '',
          measurementUnitId: conv.measurementUnitId ?? undefined,
          measurementUnitName: unitName || undefined,
          conversion: conv,
        };
        setConfirmed(selection);
        setBrowsing(false);
        setFilterIsEdit(true);
        onChangeRef.current(selection);
      })
      .catch(() => {
        if (cancelled) return;
        formik.setValues(emptyConversionFormikValues());
        setConfirmed(null);
        setBrowsing(false);
        setFilterIsEdit(false);
        onChangeRef.current(null);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialConversionId]);

  // Confirm automatically whenever the cascade resolves a factor.
  const returnId = typeof formik.values.returnId === 'string' ? formik.values.returnId.trim() : '';
  useEffect(() => {
    if (!returnId) {
      if (confirmed) {
        setConfirmed(null);
        onChangeRef.current(null);
      }
      return;
    }
    if (confirmed?.conversionId === returnId) return;
    let cancelled = false;
    conversionService.getConversionByPk(returnId)
      .then(conv => {
        if (cancelled) return;
        const selection: ConversionSelection = {
          conversionId: conv.id,
          groupId: typeof formik.values.groupId === 'string' ? formik.values.groupId.trim() : '',
          measurementUnitId: formik.values.measurementUnitId ? String(formik.values.measurementUnitId) : undefined,
          measurementUnitName: formik.values.measurementUnitName || undefined,
          conversion: conv,
        };
        setConfirmed(selection);
        setBrowsing(false);
        onChangeRef.current(selection);
      })
      .catch(() => undefined);
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnId]);

  const handleGroupChange = (event: SelectChangeEvent<string>) => {
    const id = event.target.value;
    setFilterIsEdit(false);
    if (!id) {
      formik.setValues(emptyConversionFormikValues());
      return;
    }
    const keepSource = formik.values.conversionSourceId;
    formik.setValues({
      ...emptyConversionFormikValues(),
      groupId: id,
      ...(keepSource ? { conversionSourceId: keepSource } : {}),
    });
  };

  const handleSourceChange = (event: SelectChangeEvent<string>) => {
    const id = event.target.value;
    const gid = formik.values.groupId;
    setFilterIsEdit(false);
    if (!id) {
      formik.setValues({ ...emptyConversionFormikValues(), groupId: gid });
      return;
    }
    formik.setValues({
      ...emptyConversionFormikValues(),
      groupId: gid,
      conversionSourceId: id,
    });
  };

  const showSummary = confirmed && !browsing;

  return (
    <Stack spacing={2}>
      <FormControl size='small' fullWidth variant='outlined' disabled={disabled}>
        <InputLabel>Group</InputLabel>
        <Select
          label='Group'
          value={formik.values.groupId}
          onChange={handleGroupChange}
        >
          <MenuItem value=''><em>Select a group…</em></MenuItem>
          {sortGroupsByGhgCategory(emissionInventoryGroups)
            .map(g => (
              <MenuItem key={g.id} value={g.id}>{formatGroupLabel(g)}</MenuItem>
            ))}
        </Select>
      </FormControl>
      <FormControl size='small' fullWidth variant='outlined' disabled={disabled || !formik.values.groupId}>
        <InputLabel>Conversion factor source</InputLabel>
        <Select
          name='conversionSourceId'
          label='Conversion factor source'
          value={formik.values.conversionSourceId}
          onChange={handleSourceChange}
        >
          <MenuItem value=''><em>None</em></MenuItem>
          {conversionSources
            .slice()
            .sort((a, b) => a.source.localeCompare(b.source))
            .map(s => (
              <MenuItem key={s.id} value={s.id}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  <span>{s.source}</span>
                  {s.country && (
                    <img loading='lazy' width={20} src={s.country.imageUrl} alt={`${s.country.name} flag`} />
                  )}
                </Stack>
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      {formik.values.groupId && formik.values.conversionSourceId ? (
        showSummary ? (
          <Paper
            variant='outlined'
            sx={{
              p: 2,
              borderRadius: 2,
              bgcolor: theme => (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'grey.50'),
            }}
          >
            <Typography variant='overline' color='primary' sx={{ letterSpacing: 0.06, fontWeight: 600 }}>
              Selected conversion factor
            </Typography>
            <Typography variant='subtitle2' sx={{ mt: 1, fontWeight: 600, lineHeight: 1.45 }}>
              {humanConversionPath(confirmed.conversion)}
            </Typography>
            <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1.5 }}>
              {conversionSources.find(
                s => s.id === (confirmed.conversion.conversionSourceId ?? formik.values.conversionSourceId),
              )?.source ?? 'Conversion source'}
              {' · Year '}{confirmed.conversion.calendarYear ?? calendarYear}
              {confirmed.measurementUnitName ? ` · per ${confirmed.measurementUnitName}` : ''}
            </Typography>
            <Typography variant='body2' sx={{ mb: 1.5 }}>
              <Typography component='span' variant='body2' color='text.secondary'>Multiplier (kg CO₂e per activity unit){' '}</Typography>
              <Typography component='span' variant='body2' fontWeight={700}>
                {formatMultiplier(confirmed.conversion.multiplier)}
              </Typography>
            </Typography>
            {!disabled && (
              <Link
                component='button'
                type='button'
                variant='body2'
                underline='hover'
                onClick={() => { setBrowsing(true); setFilterIsEdit(true); }}
                sx={{ cursor: 'pointer', fontSize: '0.8125rem' }}
              >
                Change conversion factor
              </Link>
            )}
          </Paper>
        ) : (
          <EmissionsFilterLogic
            categoryId={formik.values.categoryId}
            groupId={formik.values.groupId}
            calendarYear={calendarYear}
            defaultConversionSourceId={formik.values.conversionSourceId}
            formik={formik}
            isEdit={filterIsEdit && Boolean(formik.values.returnId)}
            direction='column'
            size='small'
            allowCategorySelect
            // The picker already shows its own confirmed-factor card with a
            // "Change conversion factor" affordance; a second summary inside the
            // filter would make the user click Change twice to reach the search.
            showSelectionSummary={false}
          />
        )
      ) : (
        <FormControl size='small' fullWidth variant='outlined'>
          <InputLabel>Category & detail filters</InputLabel>
          <Select label='Category & detail filters' disabled value=''>
            <MenuItem value=''>
              {formik.values.groupId ? 'Choose a conversion factor source' : 'Select a group first'}
            </MenuItem>
          </Select>
        </FormControl>
      )}
    </Stack>
  );
};
